import { FormHelperText, styled } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import React from 'react'
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form'

import StripeInput, { StripeComponent } from '@/app/component/atom/stripe/input'

type Props<T extends FieldValues> = {
  name: Path<T>
  label: string
  component: StripeComponent
}

export const RHFStripeElement = <T extends FieldValues>({ name, label, component }: Props<T>) => {
  const { control } = useFormContext<T>()

  const id = `rhf-stripe-element-${name}`

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <div>
          <StyledFormControl error={!!error} variant="standard" fullWidth>
            <InputLabel htmlFor={id} shrink>
              {label}
            </InputLabel>
            <Input
              defaultValue={value}
              fullWidth
              inputComponent={StripeInput}
              inputProps={{ component, id }}
              sx={{
                mt: 0,
              }}
              onBlur={onBlur}
              onChange={
                ((e: { complete?: boolean; empty?: boolean; error?: { message?: string } | string | undefined }) => {
                  const complete = !!e?.complete
                  const empty = !!e?.empty
                  const err = getError(e)
                  onChange({ complete, empty, error: err })
                }) as unknown as React.ChangeEventHandler<HTMLInputElement>
              }
            />
            <FormHelperText>{error ? error.message : ' '}</FormHelperText>
          </StyledFormControl>
        </div>
      )}
    />
  )
}

type ChangeEvent = { empty?: boolean; error?: { message?: string } | string | undefined }

const getError = (event: ChangeEvent) => {
  if (typeof event.error === 'string') {
    return event.error
  }

  if (event.error && typeof event.error === 'object' && 'message' in event.error) {
    return event.error.message
  }

  return undefined
}

const StyledFormControl = styled(FormControl)({
  marginBottom: 16,
})
